/**
 * Warehouse composer (new + edit) — V1.0.x inventory location editor.
 *
 * Handles both new and edit mode via `loadWarehouseId` prop.
 * Layout: flex-column h-full with static header, scrollable body, static footer.
 * No `position: sticky` — uses same flex-chain approach as warehouse-detail-page.
 *
 * @see apps/web/src/features/inventory/warehouses/warehouse-detail-page.tsx — layout reference
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Warehouse } from "lucide-react";
import { trpc } from "../../../lib/trpc.ts";
import {
  EMPTY_WAREHOUSE_FORM,
  WarehouseFormFields,
  type WarehouseFormValues,
} from "./warehouse-form-fields.tsx";
import "./warehouse-composer-page.css";

export interface WarehouseComposerPageProps {
  loadWarehouseId?: string;
}

export function WarehouseComposerPage({ loadWarehouseId }: WarehouseComposerPageProps) {
  const navigate = useNavigate();
  const isEdit = Boolean(loadWarehouseId);

  const loaded = trpc.warehouses.get.useQuery(
    { id: loadWarehouseId ?? "" },
    { enabled: isEdit },
  );
  const utils = trpc.useUtils();

  const [values, setValues] = useState<WarehouseFormValues>(EMPTY_WAREHOUSE_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !loaded.data) return;
    const d = loaded.data;
    setValues({
      name: d.name,
      code: d.code ?? "",
      description: d.description ?? "",
      warehouseType: d.warehouseType,
      isActive: d.isActive,
      isDefault: d.isDefault,
    });
  }, [isEdit, loaded.data]);

  const create = trpc.warehouses.create.useMutation();
  const update = trpc.warehouses.update.useMutation();

  const onPatch = <K extends keyof WarehouseFormValues>(key: K, value: WarehouseFormValues[K]) => {
    setValues((v) => ({ ...v, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.name.trim()) return;
    setError(null);
    setSubmitting(true);
    try {
      if (isEdit && loadWarehouseId) {
        await update.mutateAsync({
          id: loadWarehouseId,
          name: values.name.trim(),
          code: values.code.trim() || undefined,
          description: values.description.trim() || undefined,
          warehouseType: values.warehouseType,
          isActive: values.isActive,
          isDefault: values.isDefault,
        });
        await utils.warehouses.list.invalidate();
        await utils.warehouses.get.invalidate({ id: loadWarehouseId });
        void navigate({ to: "/inventory/warehouses/$warehouseId", params: { warehouseId: loadWarehouseId } });
      } else {
        const result = await create.mutateAsync({
          name: values.name.trim(),
          code: values.code.trim() || undefined,
          description: values.description.trim() || undefined,
          warehouseType: values.warehouseType,
          isDefault: values.isDefault,
        });
        await utils.warehouses.list.invalidate();
        void navigate({ to: "/inventory/warehouses/$warehouseId", params: { warehouseId: result.id } });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="warehouse-composer-page">
      {/* ── Header — static, never scrolls ────────────────────────── */}
      <header className="wc-head">
        <Link to="/inventory/warehouses" className="wc-head-back" aria-label="Back to warehouses">
          <ArrowLeft size={16} strokeWidth={2.2} />
        </Link>
        <div className="wc-head-icon" aria-hidden>
          <Warehouse size={16} />
        </div>
        <div className="wc-head-text">
          <h1>{isEdit ? "Edit warehouse" : "New warehouse"}</h1>
        </div>
        <span className="wc-status-chip">{isEdit ? "Editing" : "New"}</span>
      </header>

      {/* ── Scrollable form body ───────────────────────────────────── */}
      <form
        id="wc-form"
        className="wc-body"
        onSubmit={(e) => void handleSubmit(e)}
      >
        {error ? (
          <div
            role="alert"
            className="border border-rose-200 bg-rose-50 text-bad rounded-md p-3 text-sm"
          >
            {error}
          </div>
        ) : null}

        <div className="wc-section">
          <div className="wc-section-head">
            <span className="wc-step">1</span>
            <div>
              <h3>Details</h3>
              <span className="wc-why">Name, type, and optional description.</span>
            </div>
          </div>
          <div className="wc-section-body">
            {/* Shared with the product form's WarehouseCreateDialog (#268). */}
            <WarehouseFormFields
              values={values}
              onPatch={onPatch}
              mode={isEdit ? "edit" : "create"}
            />
          </div>
        </div>
      </form>

      {/* ── Footer — static, never scrolls ────────────────────────── */}
      <footer className="wc-foot">
        <span className="wc-foot-hint">
          <strong>Required:</strong> name only.
        </span>
        <div className="wc-foot-actions">
          <button
            type="button"
            className="inline-flex items-center h-9 px-4 rounded-md border border-line bg-paper text-sm font-semibold text-ink-2 hover:bg-bg"
            onClick={() => void navigate({ to: "/inventory/warehouses" })}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="wc-form"
            className="inline-flex items-center h-9 px-4 rounded-md bg-ink text-paper text-sm font-semibold hover:bg-ink-2 disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={submitting || !values.name.trim()}
          >
            {submitting ? "Saving…" : isEdit ? "Save changes" : "Save warehouse"}
          </button>
        </div>
      </footer>
    </div>
  );
}
